import type { WorkoutSegment } from "@runlens/shared";
import {
  SunIcon,
  BoltIcon,
  MoonIcon,
  FireIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";

interface WorkoutSegmentsProps {
  segments: WorkoutSegment[];
}

const segmentConfig: Record<
  string,
  { icon: typeof SunIcon; label: string; color: string; bgColor: string }
> = {
  warmup: {
    icon: SunIcon,
    label: "Warm-up",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
  },
  main: {
    icon: FireIcon,
    label: "Main",
    color: "text-orange-600",
    bgColor: "bg-orange-50",
  },
  intervals: {
    icon: BoltIcon,
    label: "Intervals",
    color: "text-red-600",
    bgColor: "bg-red-50",
  },
  cooldown: {
    icon: MoonIcon,
    label: "Cool-down",
    color: "text-blue-600",
    bgColor: "bg-blue-50",
  },
};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
}

function formatIntensity(intensity?: string): string {
  if (!intensity) return "";
  // Capitalize first letter
  return intensity.charAt(0).toUpperCase() + intensity.slice(1);
}

export function WorkoutSegments({ segments }: WorkoutSegmentsProps) {
  if (!segments || segments.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700 mb-2">Workout Structure</h3>
      <ol className="space-y-2">
        {segments.map((segment, index) => {
          const config = segmentConfig[segment.type] || segmentConfig.main;
          const Icon = config.icon;

          return (
            <li
              key={index}
              className={`flex items-start gap-3 p-3 rounded-lg ${config.bgColor}`}
            >
              <div className={`p-1.5 bg-white/60 rounded-lg shrink-0`}>
                <Icon className={`w-4 h-4 ${config.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium text-sm ${config.color}`}>
                    {config.label}
                  </span>
                  <span className="text-sm text-gray-600">
                    {formatDuration(segment.duration)}
                  </span>
                  {segment.intensity && (
                    <span className="text-sm text-gray-500">
                      {formatIntensity(segment.intensity)}
                    </span>
                  )}
                  {segment.hrTarget && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <HeartIcon className="w-3 h-3" />
                      ≤{segment.hrTarget}
                    </span>
                  )}
                </div>

                {/* Interval details */}
                {segment.type === "intervals" && segment.reps && (
                  <div className="mt-1 text-sm text-gray-600">
                    {segment.reps} × {segment.repDuration} min
                    {segment.recovery && (
                      <span className="text-gray-400">
                        {" / "}{segment.recovery} min recovery
                      </span>
                    )}
                  </div>
                )}

                {/* Notes */}
                {segment.notes && (
                  <p className="mt-1 text-xs text-gray-500 italic">
                    {segment.notes}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
